import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import {
  DOCTOR_APPTS_QUERY_KEY,
  listMyAppointmentsDoctor,
  normalizeAppointmentList,
} from "@/api/appointmentApi";
import { getDoctorProfile } from "@/api/userApi";
import {
  browserTimeZone,
  clinicTimeZoneOf,
  clinicZoneShortLabel,
  formatInstantInBrowserZone,
  formatInstantInClinicZone,
} from "@/lib/clinicTime";
import { ymdTodayInAppointmentZone } from "@/lib/appointmentCalendar";
import { formatInTimeZone } from "date-fns-tz";
import { PageHeader } from "@/components/common/PageHeader";
import { AppointmentStatusBadge } from "@/components/appointment/AppointmentStatusBadge";

export default function DoctorDashboard() {
  const { fullName, username } = useAuth();
  const { data: profile } = useQuery({
    queryKey: ["doctor-profile"],
    queryFn: getDoctorProfile,
  });
  const { data: raw } = useQuery({
    queryKey: DOCTOR_APPTS_QUERY_KEY,
    queryFn: listMyAppointmentsDoctor,
  });
  const data = useMemo(() => normalizeAppointmentList(raw), [raw]);
  const profileStatus = profile?.profileStatus ?? "INCOMPLETE";
  const needsAttention = profileStatus !== "APPROVED";

  const clinicZone = clinicTimeZoneOf(profile?.availabilityTimeZone);
  const clinicLabel = clinicZoneShortLabel(clinicZone);
  const now = Date.now();
  // "Today" must be the clinic calendar day, not the browser's, so a doctor
  // whose browser is in a different time zone still sees consultations
  // bucketed by the day the slot was scheduled for.
  const todayYmdClinic = ymdTodayInAppointmentZone();
  const today = data.filter((a) => {
    const t = new Date(a.slotStart).getTime();
    if (!Number.isFinite(t)) return false;
    const slotYmdClinic = formatInTimeZone(
      new Date(a.slotStart),
      clinicZone,
      "yyyy-MM-dd",
    );
    const sameDay = slotYmdClinic === todayYmdClinic && t >= now - 3600_000;
    return sameDay && a.status !== "CANCELLED";
  });
  const upcoming = data.filter(
    (a) =>
      new Date(a.slotStart).getTime() > now && a.status === "CONFIRMED",
  );
  const completed = data.filter((a) => a.status === "COMPLETED");

  return (
    <Box>
      <PageHeader
        title={`Hello${fullName || username ? `, Dr. ${fullName || username}` : ""}`}
        subtitle={`Your consultations at a glance. Times shown in your zone (${browserTimeZone()}); the clinic publishes in ${clinicZone} (${clinicLabel}).`}
      />

      {profileStatus === "SUSPENDED" && (
        <Alert
          severity="error"
          variant="filled"
          sx={{
            mb: 3,
            fontSize: "1rem",
            fontWeight: 600,
            alignItems: "center",
            boxShadow: 2,
          }}
        >
          Your account has been suspended by the administrator.
          {profile?.suspensionReason
            ? ` Reason: ${profile.suspensionReason}.`
            : ""}{" "}
          Your profile is hidden from patients and all scheduled appointments
          have been cancelled. Please contact support if you believe this is a
          mistake.
        </Alert>
      )}

      {needsAttention && profileStatus !== "SUSPENDED" && (
        <Alert
          severity={profileStatus === "REJECTED" ? "error" : "warning"}
          variant="filled"
          icon={
            profileStatus === "PENDING_REVIEW" ? (
              <HourglassTopIcon fontSize="inherit" />
            ) : undefined
          }
          sx={{
            mb: 3,
            fontSize: "1rem",
            fontWeight: 600,
            alignItems: "center",
            boxShadow: 2,
          }}
          action={
            <Button
              component={RouterLink}
              to="/doctor/onboarding"
              color="inherit"
              variant="outlined"
              size="small"
              sx={{ borderColor: "rgba(255,255,255,0.7)", fontWeight: 700 }}
            >
              {profileStatus === "PENDING_REVIEW"
                ? "View status"
                : "Complete now"}
            </Button>
          }
        >
          {profileStatus === "INCOMPLETE" &&
            "Complete your profile and submit your medical-council registration for admin review. Patients won't see you until you're approved."}
          {profileStatus === "PENDING_REVIEW" &&
            "Your profile is under admin review. We usually respond within 2 business days."}
          {profileStatus === "REJECTED" && (
            <>
              Your verification was rejected
              {profile?.rejectionReason
                ? `: ${profile.rejectionReason}`
                : "."}{" "}
              Update your details and resubmit.
            </>
          )}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Stat
          icon={<EventAvailableIcon />}
          label="Today"
          value={today.length}
        />
        <Stat icon={<AccessTimeIcon />} label="Upcoming" value={upcoming.length} />
        <Stat icon={<CheckCircleIcon />} label="Completed" value={completed.length} />
      </Grid>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Today's consultations
      </Typography>
      {today.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            No consultations scheduled for today.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {today.map((a) => (
            <Paper key={a.id} variant="outlined" sx={{ p: 2.5 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={2}
              >
                <Box>
                  <Typography fontWeight={700}>
                    {a.patientName ?? "Patient"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatInstantInBrowserZone(
                      new Date(a.slotStart),
                      "h:mm a",
                    )}{" "}
                    (
                    {formatInstantInClinicZone(
                      new Date(a.slotStart),
                      "h:mm a",
                      a.timeZone ?? clinicZone,
                    )}{" "}
                    {clinicZoneShortLabel(a.timeZone ?? clinicZone)}) ·{" "}
                    {a.patientNote ? `"${a.patientNote}"` : "No note"}
                  </Typography>
                </Box>
                <AppointmentStatusBadge status={a.status} />
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
}

function Stat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <Grid item xs={12} sm={4}>
      <Card>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: "primary.main",
                color: "#fff",
                display: "grid",
                placeItems: "center",
              }}
            >
              {icon}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Typography variant="h4" fontWeight={800}>
                {value}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
}
